import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import BrandLogo from './BrandLogo';
import {
  IconDashboard,
  IconUsers,
  IconRefresh,
  IconFilm,
  IconBank,
  IconCash,
  IconChat,
  IconSettings,
  IconSun,
  IconMoon,
  IconLogout,
  IconX,
  IconChart,
  IconSparkles,
} from './Icons';
import { productPlanLabel } from '../utils/plans';

const navGroups = [
  {
    label: 'Negocio',
    items: [
      { to: '/', label: 'Panel', Icon: IconDashboard },
      { to: '/clientes', label: 'Clientes', Icon: IconUsers },
      { to: '/suscripciones', label: 'Suscripciones', Icon: IconRefresh },
    ],
  },
  {
    label: 'Inventario',
    items: [
      { to: '/banco', label: 'Banco de cuentas', Icon: IconBank },
      { to: '/catalogo', label: 'Catálogo', Icon: IconFilm },
    ],
  },
  {
    label: 'Finanzas',
    items: [
      { to: '/renovaciones', label: 'Renovaciones', Icon: IconCash },
      { to: '/recordatorios', label: 'Recordatorios', Icon: IconChat },
    ],
  },
  {
    label: 'Crecimiento',
    items: [
      { to: '/pro', label: 'Centro Pro', Icon: IconChart, badge: 'PRO' },
      { to: '/agente', label: 'Agente IA', Icon: IconSparkles, badge: 'AI' },
    ],
  },
];

const settingsItem = { to: '/ajustes', label: 'Ajustes', Icon: IconSettings };
const allNav = [...navGroups.flatMap((group) => group.items), settingsItem];
const mobilePrimary = allNav.filter((item) => ['/', '/clientes', '/suscripciones', '/banco'].includes(item.to));

function UserAvatar({ user, small = false }) {
  const initials = (user.businessName || user.name || 'SC').slice(0, 2).toUpperCase();
  return (
    <span className={`profile-avatar ${small ? 'is-small' : ''} ${user.avatar ? 'has-image' : ''}`}>
      {user.avatar ? <img src={user.avatar} alt={`Foto de ${user.name}`} /> : initials}
    </span>
  );
}

function NavItem({ item, onClick, mobile = false }) {
  const { to, label, Icon, badge } = item;
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      className={({ isActive }) => `${mobile ? 'mobile-nav-item' : 'sidebar-nav-item'} ${isActive ? 'is-active' : ''}`}
    >
      <span className="nav-icon"><Icon size={mobile ? 20 : 18} /></span>
      <span>{label}</span>
      {badge && !mobile && <small className={`nav-plan-badge is-${badge.toLowerCase()}`}>{badge}</small>}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileMore, setMobileMore] = useState(false);

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="sidebar-head">
          <BrandLogo />
          <button onClick={toggle} title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'} className="theme-button">
            {theme === 'dark' ? <IconSun size={17} /> : <IconMoon size={17} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navGroups.map((group) => (
            <div className="sidebar-group" key={group.label}>
              <div className="sidebar-label">{group.label}</div>
              {group.items.map((item) => <NavItem key={item.to} item={item} />)}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <NavItem item={settingsItem} />
          <div className="sidebar-profile">
            <UserAvatar user={user} />
            <div className="min-w-0 flex-1">
              <strong>{user.businessName || user.name}</strong>
              <span>Plan {productPlanLabel(user.productPlan)} · {user.plan === 'anual' ? 'anual' : 'mensual'}</span>
            </div>
            <button onClick={logout} title="Cerrar sesión"><IconLogout size={17} /></button>
          </div>
        </div>
      </aside>

      <header className="mobile-topbar">
        <BrandLogo compact />
        <div className="flex items-center gap-2">
          <button onClick={toggle} className="theme-button">{theme === 'dark' ? <IconSun size={17} /> : <IconMoon size={17} />}</button>
          <UserAvatar user={user} small />
        </div>
      </header>

      <main className="tenant-main">
        <div className="tenant-content">{children}</div>
      </main>

      <nav className="mobile-bottom-nav">
        {mobilePrimary.map((item) => <NavItem key={item.to} item={item} mobile onClick={() => setMobileMore(false)} />)}
        <button className={`mobile-nav-item ${mobileMore ? 'is-active' : ''}`} onClick={() => setMobileMore(true)}>
          <span className="mobile-more-dots"><i /><i /><i /></span>
          <span>Más</span>
        </button>
      </nav>

      {mobileMore && (
        <div className="mobile-sheet-backdrop" onClick={() => setMobileMore(false)}>
          <section className="mobile-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-head">
              <div>
                <div className="eyebrow">Navegación</div>
                <h2>Más opciones</h2>
              </div>
              <button onClick={() => setMobileMore(false)}><IconX size={20} /></button>
            </div>
            <div className="mobile-sheet-grid">
              {allNav.filter((item) => !mobilePrimary.some((primary) => primary.to === item.to)).map((item) => {
                const Icon = item.Icon;
                return (
                  <NavLink key={item.to} to={item.to} onClick={() => setMobileMore(false)}>
                    <span><Icon size={20} /></span>{item.label}
                  </NavLink>
                );
              })}
            </div>
            <button className="mobile-logout" onClick={logout}><IconLogout size={17} /> Cerrar sesión</button>
          </section>
        </div>
      )}
    </div>
  );
}
